import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import axios from 'axios';
import './AccountSales.css';

const AccountSales = ({ initialParams, onParamChange }) => {
  const [accounts, setAccounts] = useState([]);
  const navigate = useNavigate();

  // ====== Lab vs Admin Logic ======
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState('lab');

  useEffect(() => {
    const storedLabId = localStorage.getItem('userLabId');
    const storedUserType = localStorage.getItem('userType');

    if (storedLabId) setLabId(storedLabId);
    if (storedUserType) {
      setUserType(storedUserType);
    } else if (storedLabId) {
      setUserType('lab');
    } else {
      setUserType('admin');
    }
  }, []);

  // ====== Fetch Accounts with Lab/Admin Filtering ======
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        let fetchedAccounts = [];

        if (userType === 'lab' && labId) {
          const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`);
          if (response.data.success) {
            fetchedAccounts = response.data.data || [];
          }
        } else {
          const [hmsRes, ddxRes] = await Promise.all([
            axios.get("https://hms.backend.hiragroupcloud.com/api/account/account"),
            axios.get("https://hms.backend.hiragroupcloud.com/api/users")
          ]);

          const hmsAccounts = (hmsRes.data.data || []).map(acc => ({
            _id: acc._id,
            account_name: acc.account_name || "Unnamed Account"
          }));

          const ddxAccounts = (ddxRes.data.users || []).map(user => ({
            _id: user._id,
            account_name: user.practiceName || `${user.firstName} ${user.lastName}`
          }));

          fetchedAccounts = [...hmsAccounts, ...ddxAccounts];
        }

        const cleanAccounts = fetchedAccounts
          .filter(acc => acc && acc.account_name)
          .map(acc => ({ _id: acc._id, account_name: acc.account_name.trim() }));

        setAccounts(cleanAccounts);
      } catch (error) {
        console.error("Error fetching accounts:", error);
      }
    };

    if (userType && (userType === 'admin' || labId)) {
      fetchAccounts();
    }
  }, [userType, labId]);

  // Current month dates
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return { startDate: formatDate(firstDay), endDate: formatDate(lastDay) };
  };

  const generateCSV = async (params) => {
    try {
      const response = await fetch("https://hms.backend.hiragroupcloud.com/api/cases/cases");
      const casesData = await response.json();

      let filteredCases = casesData.filter(c => c.invoiceNumber !== null);

      if (params.accounts) {
        filteredCases = filteredCases.filter(c => c.account_name === params.accounts);
      }

      if (params.current_start_date && params.current_end_date) {
        const start = new Date(params.current_start_date);
        const end = new Date(params.current_end_date);
        filteredCases = filteredCases.filter(c => {
          const caseDate = new Date(c.date);
          return caseDate >= start && caseDate <= end;
        });
      }

      const accountGroups = {};
      filteredCases.forEach(c => {
        if (!accountGroups[c.account_name]) accountGroups[c.account_name] = [];
        accountGroups[c.account_name].push(c);
      });

      const accountTotals = {};
      Object.keys(accountGroups).forEach(accountName => {
        accountTotals[accountName] = accountGroups[accountName].reduce(
          (sum, c) => sum + (c.totalPrice || 0), 0
        );
      });

      const csvData = [
        ['Account #', 'Account Name', 'Account Address', 'Provider', 'Invoice #', 'Invoice Date', 'Amount', 'Account Total']
      ];

      Object.entries(accountGroups).forEach(([accountName, accountCases]) => {
        const account = accounts.find(a => a.account_name === accountName) || {};

        accountCases.forEach(c => {
          csvData.push([
            account._id || '',
            accountName,
            account.address || '',
            c.provider_name || '',
            `Case Invoice # ${c.invoiceNumber} / ${c.last_name}`,
            new Date(c.date).toISOString().split('T')[0],
            c.totalPrice || 0,
            accountTotals[accountName].toFixed(2)
          ]);
        });
      });

      return csvData.map(row => row.join(',')).join('\n');
    } catch (error) {
      console.error("Error generating CSV:", error);
      return '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const params = Object.fromEntries(formData.entries());

    if (params.format === "excel") {
      const csvContent = await generateCSV(params);
      if (csvContent) {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'account_sales_report.csv');
        link.click();
        URL.revokeObjectURL(url);
      }
    } else {
      if (typeof onParamChange === 'function') {
        onParamChange(params);
      } else {
        navigate('/Addaccountsale', { state: params });
      }
    }
  };

  const { startDate, endDate } = getCurrentMonthDates();
  const currentStartDate = initialParams?.current_start_date || startDate;
  const currentEndDate = initialParams?.current_end_date || endDate;
  const selectedAccount = initialParams?.accounts || "";
  const selectedFormat = initialParams?.format || "pdf";

  return (
    <div className="acs-wrap">
      <h2 className="acs-title">Parameters</h2>

      <form className="acs-panel" id="sales-comparison-form" onSubmit={handleSubmit}>
        <div className="acs-row">
          <label className="acs-label" htmlFor="acs-start">
            Start Date <span className="acs-req">*</span>
          </label>
          <div className="acs-field">
            <input
              id="acs-start"
              className="acs-input acs-date"
              type="date"
              name="current_start_date"
              required
              defaultValue={currentStartDate}
            />
          </div>
        </div>

        <div className="acs-row">
          <label className="acs-label" htmlFor="acs-end">
            End Date <span className="acs-req">*</span>
          </label>
          <div className="acs-field">
            <input
              id="acs-end"
              className="acs-input acs-date"
              type="date"
              name="current_end_date"
              required
              defaultValue={currentEndDate}
            />
          </div>
        </div>

        <div className="acs-row">
          <label className="acs-label" htmlFor="acs-group">Group By</label>
          <div className="acs-field">
            <select id="acs-group" className="acs-input acs-select" name="practice">
              <option value="">No Grouping</option>
              <option value="71159">State</option>
            </select>
          </div>
        </div>

        <div className="acs-row">
          <label className="acs-label" htmlFor="acs-accounts">Accounts</label>
          <div className="acs-field">
            <select
              id="acs-accounts"
              className="acs-input acs-select"
              name="accounts"
              defaultValue={selectedAccount}
            >
              <option value="">Select accounts</option>
              {accounts.map((acc) => (
                <option key={acc._id} value={acc.account_name}>
                  {acc.account_name}
                </option>
              ))}
            </select>
            <label className="acs-check">
              <input type="checkbox" name="include_closed" /> Include Closed
            </label>
          </div>
        </div>

        <div className="acs-row">
          <label className="acs-label" htmlFor="acs-tags">Account Tags</label>
          <div className="acs-field">
            <select id="acs-tags" className="acs-input acs-select" name="account_tags">
              <option value="">No filter</option>
            </select>
          </div>
        </div>

        <div className="acs-row">
          <span className="acs-label" />
          <div className="acs-field">
            <label className="acs-check acs-check-first">
              <input type="checkbox" name="include_finance_charge" /> Include Finance Charge
            </label>
          </div>
        </div>

        <div className="acs-row">
          <label className="acs-label" htmlFor="acs-format">
            Format <span className="acs-req">*</span>
          </label>
          <div className="acs-field">
            <select
              id="acs-format"
              className="acs-input acs-select"
              name="format"
              required
              defaultValue={selectedFormat}
            >
              <option value="pdf">On Screen</option>
              <option value="excel">CSV(Excel)</option>
            </select>
          </div>
        </div>

        <div className="acs-row">
          <span className="acs-label" />
          <div className="acs-field">
            <button type="submit" className="acs-run">
              <FaSearch /> Run
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AccountSales;